import {
  AzureSttResponse,
  MicHandler,
  MicOptions,
  RecorderData
} from "../../../models/microphone";
import { AbstMic } from "../common";
import * as MicrosoftSDK from "microsoft-cognitiveservices-speech-sdk";
import { v4 } from "uuid";
import { filterProfanity } from "../../../utils/common/filterProfanity";
import {
  AudioInputStream,
  ConnectionMessageEventArgs,
  PushAudioInputStream,
  Recognizer,
  SpeechRecognitionEventArgs,
  SpeechRecognizer
} from "microsoft-cognitiveservices-speech-sdk";
import { uploadToS3 } from "../../../utils/common/s3";
import {
  SUBSCRIPTION_KEY,
  SERVICE_REGION
} from "../../../constants/microphone/keys";
import {
  IDetailedSpeechPhrase,
  ISimpleSpeechPhrase
} from "microsoft-cognitiveservices-speech-sdk/distrib/browser/microsoft.cognitiveservices.speech.sdk.bundle";
import { buildWavHeader } from "../../../utils/audio";

function convertPcmToWav(buffer: any) {
  // create WAV header
  const sampleRate = 16000;
  const numChannels = 1;
  const [type, format] = [Uint8Array, 1];
  const wavHeader = new Uint8Array(
    buildWavHeader({
      numFrames: buffer.byteLength,
      bytesPerSample: 2,
      sampleRate: sampleRate,
      numChannels: numChannels,
      format: format
    })
  );

  // wavHeader + raw PCM Bytes
  const wavBytes = new Uint8Array(wavHeader.length + buffer.byteLength);
  wavBytes.set(wavHeader, 0);
  wavBytes.set(new Uint8Array(buffer), wavHeader.length);
  return wavBytes;
}

const { AudioConfig, Connection, ResultReason, SpeechConfig } = MicrosoftSDK;

export type MicMicrosoftSdkOptions = MicOptions;

export class MicMicrosoftSdk extends AbstMic implements MicHandler {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------
  private _options: MicMicrosoftSdkOptions;

  /**
   * Raw data taken from the ScriptNode. Only mono channel is recorded
   */
  private _totalBuffer: Float32Array = new Float32Array(0);
  /**
   * Is window.RawAudioRecorder available
   */
  private _rawAudioRecorderAvailable: boolean;
  /**
   * stream for sending audio buffer to azure SDK
   */
  private _customStream?: PushAudioInputStream;

  private _recognitions: MicrosoftSDK.SpeechRecognitionResult[] = [];
  private _recording = false;
  private _wavFragmentCount = 0;
  private _wavFragments: { [id: number]: ArrayBuffer } = {};

  private _recognizer?: SpeechRecognizer = undefined;

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: MicMicrosoftSdkOptions = {}) {
    super(options);

    this._rawAudioRecorderAvailable = (window as any).RawAudioRecorder;

    this._options = options;
  }

  async init() {
    this._recognizingText = [];

    this.createRecognizer();

    this._status = "ready";
  }

  createRecognizer() {
    const speechConfig = SpeechConfig.fromSubscription(
      SUBSCRIPTION_KEY,
      SERVICE_REGION
    );
    speechConfig.outputFormat = 1;
    speechConfig.setProperty(
      "SpeechServiceResponse_PostProcessingOption",
      "TrueText"
    );

    let audioConfig;
    if (this._rawAudioRecorderAvailable) {
      this._customStream = AudioInputStream.createPushStream();

      audioConfig = AudioConfig.fromStreamInput(this._customStream);
    } else {
      audioConfig = AudioConfig.fromDefaultMicrophoneInput();
    }
    this._recognizer = new SpeechRecognizer(speechConfig, audioConfig);

    const connection = Connection.fromRecognizer(this._recognizer);

    connection.messageSent = this.messageSent;

    this._recognizer.recognizing = this.recognizing;

    this._recognizer.recognized = this.recognized;
  }

  messageSent = (args: ConnectionMessageEventArgs) => {
    try {
      if (this._recording) {
        if (args.message.path === "audio" && args.message.isBinaryMessage) {
          this._wavFragments[this._wavFragmentCount++] =
            args.message.binaryMessage;

          const wavFragmentEntries = Object.entries(this._wavFragments);

          let silenceDetected = false;
          if (wavFragmentEntries.length === 10) {
            silenceDetected = wavFragmentEntries.every(([, wavFragments]) => {
              const array = new Uint8Array(wavFragments);
              return array.every(value => {
                return value === 0;
              });
            });
          }

          if (silenceDetected) {
            this.onSilenceDetected();
            return;
          }
        }
      }
    } catch (error) {
      console.log("connection.messageSent error:", error);
    }
  };

  async onSilenceDetected(): Promise<void> {
    this.stop();
    this._onSilenceDetected();
    await this.cancel();
    this.clearRecording();
    this._status = "ready";
  }

  recognizing = (s: Recognizer, e: SpeechRecognitionEventArgs) => {
    try {
      const result = JSON.parse(e.result.json);

      this._recognizingText = [
        `${this.currentRecognitions()} ${filterProfanity(result.Text)}`
      ];
    } catch (error) {
      console.log("recognizer.recognizing error:", error);
    }
  };

  recognized = (s: Recognizer, e: SpeechRecognitionEventArgs) => {
    try {
      if (e.result.reason == ResultReason.RecognizedSpeech) {
        if (e.result.text) {
          this._recognitions = [...this._recognitions, e.result];
        }
      }
    } catch (error) {
      console.log("recognizer.recognized error:", error);
    }
  };

  // --------------------------------------------------------------------------
  //    Methods: Microphone
  // --------------------------------------------------------------------------
  /**
   * Start recording and recognizing sending
   *
   * --- Error handling ---
   * > MediaStream error most likely is due to "permission denial".
   * The status is then switching to "init-err" so that the user cannot re-try
   * except from re-loading the page.
   * > when "mic-err_microsoft-sdk_connection-error" is added, JzMic will emit a
   * connection-error event. This is done to make error handling easier in the consuming
   * component
   * > For other errors, status is "ready" because it might be a non-reproducible
   * error and should not, then, prevent the user from retrying
   */
  async start(): Promise<void> {
    this._status = "permission";
    this._totalBuffer = new Float32Array(0);

    this.createRecognizer();

    this._recognizer!.startContinuousRecognitionAsync(
      () => {
        this._onRecordingStarted();
        this._onRecognizingStarted();

        this._recording = true;
        this._status = "recording";
      },
      error => {
        this.log(error);
        this.addError(
          {
            key: "mic-error_microsoft-sdk_connection-error"
          },
          false
        );
        this.stop();
      }
    );

    if (this._rawAudioRecorderAvailable) {
      (window as any).RawAudioRecorder.startListening(
        undefined,
        (audioData: number[]) => {
          const array = Uint8Array.from(audioData);

          this._customStream?.write(array.buffer);
        },
        undefined,
        (error: any) => {
          console.log("RawAudioRecorder error:", error);
        }
      );
    }
  }

  /**
   * Stop recording and recognizing
   */
  async stop(): Promise<void> {
    this._recording = false;

    if (this._recognizer) {
      if (this._rawAudioRecorderAvailable) {
        (window as any).RawAudioRecorder.stopListening();
      }

      this._recognizer.stopContinuousRecognitionAsync(
        () => {
          this._recognizingText = [];
          this.processNewData();
          this.clearRecording();
          this._onRecordingStopped(this.audioData);
          this._onRecognizingStopped();
        },
        error => {
          console.log("stopContinuousRecognitionAsync error:", { error });
        }
      );
    }

    this._status = "stopped";
  }

  /**
   * Force stop a in-progress processing
   */
  async cancel(): Promise<void> {
    this._onRecordingError("Cancelled");
    this._onRecognizingError("Cancelled");

    this._status = "stopped";
    this._isProcessing = false;
  }

  async processNewData() {
    try {
      const wavBlob = this.buildAudio();
      this.clearRecording();
      const remoteUrl = uploadToS3(wavBlob!, `${v4()}.wav`);
      const recordData: RecorderData = {
        blob: wavBlob!,
        remoteUrl
      };

      this.log(`RECOGNIZED: wav - ${wavBlob}`);
      this.log(`RECOGNIZED: blob - ${remoteUrl}`);

      const recognitionData = this._recognitions.reduce<AzureSttResponse>(
        (accumulator, recognition) => {
          const json: IDetailedSpeechPhrase = JSON.parse(recognition.json);
          const best = json.NBest[0];

          const prevDuration = accumulator.IntermediateResults
            ? accumulator.IntermediateResults[0].Duration
            : 0;

          accumulator = {
            DisplayText: filterProfanity(
              `${accumulator.DisplayText || ""} ${best.Display}`
            ),
            Lexical: filterProfanity(
              `${accumulator.Lexical || ""} ${best.Lexical}`
            ),
            MaskedDisplayText: filterProfanity(
              `${accumulator.MaskedDisplayText || ""} ${best.Display}`
            ),
            MaskedLexical: filterProfanity(
              `${accumulator.MaskedLexical || ""} ${best.Lexical}`
            ),
            IntermediateResults: [
              {
                ...best,
                Confidence: best.Confidence || 0,
                Duration: prevDuration + (json.Duration || 0),
                Offset: json.Offset || 0
              }
            ],
            // eslint-disable-next-line @typescript-eslint/camelcase
            request_timed_out: false
          };

          return accumulator;
        },
        {} as AzureSttResponse
      );

      await this.addNewData(recordData, recognitionData);

      this._recognitions = [];
    } catch (error) {
      console.log("processNewData error:", error);
    }
  }

  currentRecognitions() {
    return this._recognitions
      .map(r =>
        filterProfanity((JSON.parse(r.json) as ISimpleSpeechPhrase).DisplayText)
      )
      .join(" ");
  }

  // --------------------------------------------------------------------------
  //    Methods: ScriptProcessorNode
  // --------------------------------------------------------------------------
  clearRecording() {
    this._wavFragmentCount = 0;
    this._wavFragments = {};
  }

  buildAudio() {
    try {
      let byteCount = 0;
      for (let i = 0; i < this._wavFragmentCount; i++) {
        byteCount += this._wavFragments[i].byteLength;
      }

      // Output array.
      const sentAudio: Uint8Array = new Uint8Array(byteCount);

      byteCount = 0;
      for (let i = 0; i < this._wavFragmentCount; i++) {
        sentAudio.set(new Uint8Array(this._wavFragments[i]), byteCount);
        byteCount += this._wavFragments[i].byteLength;
      }

      const wav = convertPcmToWav(sentAudio);
      //
      // // Set the file size in the wave header:
      // const view = new DataView(sentAudio.buffer);
      // view.setUint32(4, byteCount, true);
      // view.setUint32(40, byteCount, true);
      //
      // // Adding a type has no effect on blob.size
      return new Blob([wav], { type: this._azAudioType });
    } catch (error) {
      console.log("buildAudio error:", error);
    }
  }
}
